import React, { useState, useRef, useEffect } from 'react';

/**
 * REAL EMAIL SENDER
 *
 * Actually sends a real email through Resend API
 * Shows EVERYTHING that happens
 */

interface EmailEvent {
  id: string;
  timestamp: number;
  type: 'info' | 'success' | 'error' | 'process' | 'warning';
  message: string;
  details?: any;
}

interface RequirementCheck {
  name: string;
  status: 'checking' | 'pass' | 'fail' | 'pending';
  message: string;
}

export default function RealEmailSender() {
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [lastResponse, setLastResponse] = useState<any>(null);
  const [requirements, setRequirements] = useState<RequirementCheck[]>([
    { name: 'Valid Email Format', status: 'pending', message: 'Email not entered' },
    { name: 'CloudFlare Worker', status: 'pending', message: 'Not tested' },
    { name: 'RESEND_API_KEY', status: 'pending', message: 'Not tested' },
    { name: 'Network Connection', status: 'pending', message: 'Not tested' },
    { name: 'CORS Headers', status: 'pending', message: 'Not tested' }
  ]);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  useEffect(() => {
    // Check email format as user types
    if (toEmail) {
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail);
      setRequirements(prev => prev.map(req =>
        req.name === 'Valid Email Format'
          ? { ...req, status: isValid ? 'pass' : 'fail', message: isValid ? toEmail : 'Invalid format' }
          : req
      ));
    } else {
      setRequirements(prev => prev.map(req =>
        req.name === 'Valid Email Format'
          ? { ...req, status: 'pending', message: 'Email not entered' }
          : req
      ));
    }
  }, [toEmail]);

  const logEvent = (type: EmailEvent['type'], message: string, details?: any) => {
    const newEvent: EmailEvent = {
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      type,
      message,
      details
    };
    setEvents(prev => [...prev, newEvent]);
  };

  const checkRequirements = async () => {
    // Check network
    setRequirements(prev => prev.map(req =>
      req.name === 'Network Connection'
        ? { ...req, status: 'checking', message: 'Testing...' }
        : req
    ));

    try {
      // Test worker health
      const healthResponse = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/health');
      const healthData = await healthResponse.json();

      setRequirements(prev => prev.map(req => {
        if (req.name === 'Network Connection') {
          return { ...req, status: 'pass', message: 'Connected' };
        }
        if (req.name === 'CloudFlare Worker') {
          return { ...req, status: healthData.success ? 'pass' : 'fail', message: healthData.success ? 'Worker healthy' : 'Worker error' };
        }
        if (req.name === 'CORS Headers') {
          return { ...req, status: 'pass', message: 'Headers OK' };
        }
        return req;
      }));
    } catch (error) {
      setRequirements(prev => prev.map(req => {
        if (req.name === 'Network Connection') {
          return { ...req, status: 'fail', message: 'No connection' };
        }
        if (req.name === 'CloudFlare Worker') {
          return { ...req, status: 'fail', message: 'Worker unreachable' };
        }
        return req;
      }));
    }
  };

  const sendRealEmail = async () => {
    if (!toEmail) {
      logEvent('error', 'No email address provided');
      return;
    }

    setIsSending(true);
    setEvents([]);
    setLastResponse(null);
    const startTime = Date.now();

    try {
      // Check all requirements first
      await checkRequirements();

      // Step 1: Validate email
      logEvent('info', 'Validating email address', {
        email: toEmail,
        valid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail)
      });

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail)) {
        logEvent('error', 'Invalid email format');
        return;
      }

      // Step 2: Generate OTP
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      logEvent('info', 'Generated OTP code', { code });

      // Step 3: Show what Resend API offers vs what we're using
      logEvent('info', 'Resend API Capabilities', {
        'Available': {
          'Emails': ['send', 'batch', 'retrieve', 'update', 'cancel'],
          'Domains': ['add', 'verify', 'list', 'delete'],
          'API Keys': ['create', 'list', 'delete'],
          'Audiences': ['create', 'list', 'delete', 'add_contact', 'remove_contact'],
          'Broadcasts': ['send', 'list', 'retrieve'],
          'Webhooks': ['email.sent', 'email.delivered', 'email.bounced', 'email.complained']
        },
        'We Are Using': {
          'Endpoint': 'POST /emails',
          'Action': 'send',
          'Fields': ['from', 'to', 'subject', 'html', 'text']
        }
      });

      // Step 4: Prepare request
      const requestBody = {
        email: toEmail,
        code,
        testMode
      };

      logEvent('process', 'Preparing API request', {
        endpoint: 'https://futr-bet-api.b-9f2.workers.dev/api/send-email',
        method: 'POST',
        body: requestBody
      });

      // Step 5: Make actual API call
      logEvent('process', 'Calling CloudFlare Worker...');

      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();
      const elapsed = Date.now() - startTime;

      // Update API key requirement based on response
      if (responseData.error?.includes('RESEND_API_KEY')) {
        setRequirements(prev => prev.map(req =>
          req.name === 'RESEND_API_KEY'
            ? { ...req, status: 'fail', message: 'Not configured' }
            : req
        ));
      } else if (response.ok) {
        setRequirements(prev => prev.map(req =>
          req.name === 'RESEND_API_KEY'
            ? { ...req, status: 'pass', message: 'Key valid' }
            : req
        ));
      }

      logEvent(
        response.ok ? 'success' : 'error',
        response.ok ? '✅ API call successful' : '❌ API call failed',
        {
          status: response.status,
          elapsed: `${elapsed}ms`,
          response: responseData
        }
      );

      setLastResponse(responseData);

      if (response.ok && responseData.messageId) {
        logEvent('success', '🎉 EMAIL SENT SUCCESSFULLY!', {
          messageId: responseData.messageId,
          to: toEmail,
          code,
          totalTime: `${elapsed}ms`
        });

        // Step 6: What happens next
        logEvent('info', 'Email Journey', {
          '1_Queue': 'Email queued at Resend',
          '2_SMTP': 'Resend connects via SMTP',
          '3_MX_Lookup': 'DNS MX record lookup',
          '4_SPF_Check': 'SPF validation',
          '5_DKIM_Sign': 'DKIM signature added',
          '6_Delivery': 'Handed to recipient server',
          '7_Spam_Filter': 'Spam checks performed',
          '8_Inbox': 'Delivered to inbox (hopefully)'
        });
      } else {
        logEvent('error', 'Email send failed', {
          error: responseData.error || 'Unknown error',
          suggestion: responseData.error?.includes('API')
            ? 'Check RESEND_API_KEY in CloudFlare Worker'
            : 'Check worker logs for details'
        });
      }

    } catch (error) {
      logEvent('error', 'Request failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        suggestion: 'Is the CloudFlare Worker deployed?'
      });
    } finally {
      setIsSending(false);
    }
  };

  const getEventColor = (type: string) => {
    switch(type) {
      case 'success': return 'text-green-400 border-green-400';
      case 'error': return 'text-red-400 border-red-400';
      case 'warning': return 'text-orange-400 border-orange-400';
      case 'process': return 'text-yellow-400 border-yellow-400';
      default: return 'text-gray-400 border-gray-400';
    }
  };

  const getEventIcon = (type: string) => {
    switch(type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'process': return '⚡';
      default: return 'ℹ️';
    }
  };

  const getRequirementColor = (status: string) => {
    switch(status) {
      case 'pass': return 'text-green-400';
      case 'fail': return 'text-red-400';
      case 'checking': return 'text-yellow-400';
      default: return 'text-gray-600';
    }
  };

  const getRequirementIcon = (status: string) => {
    switch(status) {
      case 'pass': return '✅';
      case 'fail': return '❌';
      case 'checking': return '🔄';
      default: return '⚪';
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">📧 REAL EMAIL SENDER</h1>
          <p className="text-gray-400">Actually sends a real email (no simulation)</p>
          <p className="text-yellow-400 text-sm mt-2">One thing done perfectly</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Controls */}
          <div className="space-y-4">
            {/* Email Input */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">📮 SEND TO</h2>

              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 p-3 text-green-400 text-lg"
                placeholder="your-email@gmail.com"
                disabled={isSending}
              />

              <div className="mt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                    disabled={isSending}
                  />
                  <span className={testMode ? 'text-yellow-400' : 'text-gray-400'}>
                    Test Mode (use Resend test domain)
                  </span>
                </label>

                {testMode && (
                  <div className="text-xs text-gray-500 p-2 bg-gray-900 border border-gray-700">
                    Will send from: onboarding@resend.dev<br/>
                    (Works without domain verification)
                  </div>
                )}
              </div>
            </div>

            {/* Requirements Check */}
            <div className="border border-yellow-400 p-4">
              <h2 className="text-xl mb-3 text-yellow-400">✓ REQUIREMENTS CHECK</h2>
              <div className="space-y-2">
                {requirements.map((req, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{req.name}:</span>
                    <div className={`flex items-center gap-2 ${getRequirementColor(req.status)}`}>
                      <span>{getRequirementIcon(req.status)}</span>
                      <span>{req.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* The Button */}
            <button
              onClick={sendRealEmail}
              disabled={isSending || !toEmail}
              className={`w-full p-6 text-2xl font-bold transition-all ${
                isSending
                  ? 'bg-yellow-400 text-black animate-pulse'
                  : 'bg-green-400 text-black hover:bg-green-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSending ? '📤 SENDING...' : '🚀 SEND REAL EMAIL'}
            </button>

            {/* Response Display */}
            {lastResponse && (
              <div className={`border-2 p-4 ${
                lastResponse.success
                  ? 'border-green-400 bg-green-900/20'
                  : 'border-red-400 bg-red-900/20'
              }`}>
                <h3 className={`text-lg font-bold mb-2 ${
                  lastResponse.success ? 'text-green-400' : 'text-red-400'
                }`}>
                  {lastResponse.success ? '✅ SUCCESS!' : '❌ FAILED'}
                </h3>

                <pre className="text-xs overflow-x-auto">
{JSON.stringify(lastResponse, null, 2)}
                </pre>

                {lastResponse.messageId && (
                  <div className="mt-3 p-2 bg-black/50 border border-green-400">
                    <div className="text-yellow-400 font-bold">NOW CHECK YOUR EMAIL!</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Message ID: {lastResponse.messageId}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Resend API Info */}
            <div className="border border-purple-400 p-4">
              <h2 className="text-xl mb-3 text-purple-400">📚 RESEND API</h2>
              <div className="text-xs space-y-2">
                <div>
                  <span className="text-gray-400">We're using:</span>
                  <div className="text-green-400 mt-1">
                    • POST /emails (send single email)
                  </div>
                </div>
                <div className="mt-3">
                  <span className="text-gray-400">Also available:</span>
                  <div className="text-gray-600 mt-1">
                    • Batch sending<br/>
                    • Domain management<br/>
                    • Webhook events<br/>
                    • Email templates<br/>
                    • Audience lists<br/>
                    • Broadcast campaigns
                  </div>
                </div>
              </div>
            </div>

            {/* What This Does */}
            <div className="border border-cyan-400 p-4">
              <h2 className="text-xl mb-3 text-cyan-400">🎯 WHAT THIS ACTUALLY DOES</h2>
              <ol className="text-xs text-gray-400 space-y-2">
                <li>1. Validates your email address</li>
                <li>2. Generates a 6-digit OTP code</li>
                <li>3. Calls our CloudFlare Worker</li>
                <li>4. Worker calls Resend API</li>
                <li>5. Resend queues & sends email</li>
                <li className="text-green-400">6. Email arrives in your inbox!</li>
              </ol>
            </div>
          </div>

          {/* Right: Event Log */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 REAL-TIME LOG</h2>

            <div className="h-[800px] overflow-y-auto space-y-2 font-mono text-xs">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Enter an email and click send...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${getEventColor(event.type)}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getEventIcon(event.type)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                        </div>
                        <div className="text-gray-300 mt-1">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom Status */}
        {lastResponse?.success && (
          <div className="mt-6 border-2 border-green-400 p-6 text-center animate-pulse">
            <div className="text-3xl mb-2">🎉 🙌 ✅</div>
            <div className="text-2xl text-green-400 font-bold">EMAIL SENT SUCCESSFULLY!</div>
            <div className="text-yellow-400 mt-2">Check your inbox for the OTP code!</div>
            <div className="text-gray-400 text-sm mt-4">
              This is not a simulation. A real email was sent through Resend's API.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}